import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import { format, eachDayOfInterval, isWeekend, subDays } from "date-fns";
import { Calendar as CalendarIcon, BarChart3, TrendingUp, Users, AlertTriangle, Download, Eye, PieChart as PieChartIcon, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase/config";
import { collection, query, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Header from "./Header";
import AdminSidebar from "./Sidebar";

interface ClassData {
  id: string;
  name: string;
  teacher: string | null; // Updated to allow string or null, matching Firestore data
  teacherId: string | null;
  currentClassTeacherUid: string | null;
}

interface StudentData {
  id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  registrationNumber: string;
  currentClassUid?: string;
  photoUrl?: string; // Added
}

interface AttendanceRecord {
  id: string;
  studentId: string;
  date: string;
  status: 'present' | 'absent';
  reason?: string | null;
  markedBy: string;
  markedAt: string;
  classId: string;
}

interface TrendData {
  name: string;
  attendance: number;
  present: number;
  absent: number;
}

const getFullName = (student: { firstName: string; middleName?: string; lastName: string }) => {
  return [student.firstName, student.middleName, student.lastName].filter(Boolean).join(" ");
};

const AdminAttendanceAnalytics = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('weekly');
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'overview' | 'trends' | 'detail'>('overview');
  const [sortBy, setSortBy] = useState<'attendance' | 'name'>('attendance');
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [students, setStudents] = useState<StudentData[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [networkError, setNetworkError] = useState<boolean>(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Authentication logic
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Authenticating user with uid:`, user.uid);
          await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] ID Token Result claims:`, JSON.stringify(idTokenResult.claims, null, 2));

          let selectedAffiliation: { role: string; schoolID?: string } | null = null;
          if (defaultAffiliation && ['school_staff', 'admin'].includes(defaultAffiliation.role) && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => ['school_staff', 'admin'].includes(aff.role) && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role && ['school_staff', 'admin'].includes(selectedAffiliation.role) && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            setSchoolID(selectedAffiliation.schoolID);
            setHasValidClaims(true);
            setNetworkError(false);
          } else {
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Invalid or missing claims, retrying after 10 seconds...`);
            await new Promise(resolve => setTimeout(resolve, 10000));
            await user.getIdToken(true);
            const retryIdTokenResult = await user.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            let retrySelectedAffiliation: { role: string; schoolID?: string } | null = null;
            if (retryDefaultAff && ['school_staff', 'admin'].includes(retryDefaultAff.role) && retryDefaultAff.schoolID) {
              retrySelectedAffiliation = retryDefaultAff;
            } else if (retryAffiliations.length > 0) {
              retrySelectedAffiliation = retryAffiliations.find(aff => ['school_staff', 'admin'].includes(aff.role) && aff.schoolID) || retryAffiliations[0];
            }

            if (retrySelectedAffiliation?.role && ['school_staff', 'admin'].includes(retrySelectedAffiliation.role) && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
              setSchoolID(retrySelectedAffiliation.schoolID);
              setHasValidClaims(true);
              setNetworkError(false);
            } else {
              setHasValidClaims(false);
              setSchoolID(null);
              setNetworkError(false);
              toast({
                title: "Permission Error",
                description: "You lack the necessary permissions to view analytics. Please contact support.",
                variant: "destructive",
                duration: 5000,
              });
            }
          }
        } catch (error: any) {
          console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error verifying user claims:`, error);
          if (error.code === 'auth/network-request-failed' || error.message.includes('network')) {
            setNetworkError(true);
            toast({
              title: "Network Error",
              description: "Failed to verify permissions due to a network issue. Please check your internet connection and try again.",
              variant: "destructive",
              duration: 5000,
            });
          } else {
            setHasValidClaims(false);
            setSchoolID(null);
            setNetworkError(false);
            toast({
              title: "Authentication Error",
              description: "Failed to verify permissions. Please try again or contact support.",
              variant: "destructive",
              duration: 5000,
            });
          }
        }
      } else {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No user logged in`);
        setNetworkError(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsLoading(false);
    });

    return () => {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from onAuthStateChanged`);
      unsubscribe();
    };
  }, [navigate, toast]);

  // Fetch classes
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Skipping classes fetch: schoolID=${schoolID}, isLoading=${isLoading}, hasValidClaims=${hasValidClaims}`);
      return;
    }

    const classesRef = collection(db, `schools/${schoolID}/classes`);
    const unsubscribeClasses = onSnapshot(classesRef, (snapshot) => {
      const classList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        teacher: doc.data().teacher || null, // Fetch teacher name from Firestore
        teacherId: doc.data().teacherId || null,
        currentClassTeacherUid: doc.data().currentClassTeacherUid || null,
      }));
      setClasses(classList.sort((a, b) => a.name.localeCompare(b.name)));
      if (classList.length > 0 && !selectedClass) {
        setSelectedClass(classList[0].id);
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Set selectedClass to:`, classList[0].id);
      }
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Classes fetched:`, classList);
    }, (error) => {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching classes:`, error);
      toast({
        title: "Error",
        description: "Failed to fetch classes. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      setClasses([]);
    });

    return () => {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from classes onSnapshot`);
      unsubscribeClasses();
    };
  }, [schoolID, isLoading, hasValidClaims, selectedClass, toast]);

  // Fetch all students for the school
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Skipping students fetch: schoolID=${schoolID}, isLoading=${isLoading}, hasValidClaims=${hasValidClaims}`);
      return;
    }

    const studentsRef = collection(db, `schools/${schoolID}/students`);
    const unsubscribeStudents = onSnapshot(studentsRef, (snapshot) => {
      const studentList = snapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().firstName,
        middleName: doc.data().middleName,
        lastName: doc.data().lastName,
        registrationNumber: doc.data().registrationNumber,
        currentClassUid: doc.data().currentClassUid,
        photoUrl: doc.data().photoUrl || undefined,
      }));
      setStudents(studentList);
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] All students fetched:`, studentList);
    }, (error) => {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching students:`, error);
      toast({
        title: "Error",
        description: "Failed to fetch students. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      setStudents([]);
    });

    return () => {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from students onSnapshot`);
      unsubscribeStudents();
    };
  }, [schoolID, isLoading, hasValidClaims, toast]);

  // Fetch attendance records for analytics
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Skipping attendance fetch: schoolID=${schoolID}, isLoading=${isLoading}, hasValidClaims=${hasValidClaims}`);
      setAttendanceRecords([]);
      return;
    }

    const periodStart = subDays(selectedDate, 30);
    const periodEnd = selectedDate;
    const daysInPeriod = eachDayOfInterval({ start: periodStart, end: periodEnd });
    const unsubscribePromises: (() => void)[] = [];

    daysInPeriod.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const recordsRef = collection(db, `schools/${schoolID}/attendance/${dateStr}/records`);
      const q = query(recordsRef);

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const records = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: dateStr,
          reason: doc.data().reason ?? null,
        } as AttendanceRecord));

        setAttendanceRecords(prev => {
          const filteredRecords = prev.filter(r => r.date !== dateStr);
          return [...filteredRecords, ...records];
        });

        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Fetched records for ${dateStr}:`, records);
      }, (error) => {
        console.error(`Error fetching records for ${dateStr}:`, error);
        toast({
          title: "Error",
          description: `Failed to load attendance records for ${dateStr}. Please check Firebase console for index errors.`,
          variant: "destructive",
          duration: 5000,
        });
        console.log(`To create the necessary Firestore index for ${dateStr}, use the following configuration in your Firebase console:`);
        console.log({
          collectionGroup: `schools/${schoolID}/attendance/${dateStr}/records`,
          queryScope: 'COLLECTION',
          fields: [{ fieldPath: 'classId', order: 'ASCENDING' }],
        });
      });

      unsubscribePromises.push(unsubscribe);
    });

    return () => {
      unsubscribePromises.forEach(unsubscribe => unsubscribe());
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribed from attendance listeners`);
    };
  }, [schoolID, selectedDate, isLoading, hasValidClaims, toast]);

  // Calculate class analytics
  const classAnalytics = useMemo(() => {
    return classes.map(cls => {
      const classStudents = students.filter(s => s.currentClassUid === cls.id);
      const classRecords = attendanceRecords.filter(r => r.classId === cls.id);
      const periodStart = subDays(selectedDate, 30);
      const periodEnd = new Date(selectedDate);
      const totalRecords = classRecords.filter(r => {
        const recordDate = new Date(r.date);
        return recordDate >= periodStart && recordDate <= periodEnd;
      });

      const presentDays = totalRecords.filter(r => r.status === 'present').length;
      const absentDays = totalRecords.filter(r => r.status === 'absent').length;
      const totalMarkedDays = presentDays + absentDays;
      const avgAttendance = totalMarkedDays > 0 ? Math.round((presentDays / totalMarkedDays) * 100) : 0;

      return {
        id: cls.id,
        name: cls.name,
        teacher: cls.teacher || 'No teacher assigned', // Use the teacher field from Firestore
        studentCount: classStudents.length,
        avgAttendance,
      };
    });
  }, [classes, students, attendanceRecords, selectedDate]);

  // Calculate student analytics for detail view
  const studentAnalytics = useMemo(() => {
    if (!selectedClass) return [];
    const periodStart = subDays(selectedDate, 30);
    const periodEnd = new Date(selectedDate);

    const analytics = students
      .filter(s => s.currentClassUid === selectedClass)
      .map(student => {
        const studentRecords = attendanceRecords.filter(r => r.studentId === student.id && r.classId === selectedClass);
        const periodRecords = studentRecords.filter(r => {
          const recordDate = new Date(r.date);
          return recordDate >= periodStart && recordDate <= periodEnd;
        });

        const daysPresent = periodRecords.filter(r => r.status === 'present').length;
        const daysAbsent = periodRecords.filter(r => r.status === 'absent').length;
        const totalMarkedDays = daysPresent + daysAbsent;
        const attendance = totalMarkedDays > 0 ? Math.round((daysPresent / totalMarkedDays) * 100) : 0;

        return {
          id: student.id,
          name: getFullName(student),
          registrationNumber: student.registrationNumber,
          attendance,
          daysPresent,
          daysAbsent,
          photoUrl: student.photoUrl,
        };
      });
    console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Student analytics for class ${selectedClass}:`, analytics);
    return analytics;
  }, [students, attendanceRecords, selectedClass, selectedDate]);

  // Generate trend data
  const generateTrendData = (period: 'daily' | 'weekly' | 'monthly' | 'yearly'): TrendData[] => {
    const periodStart = subDays(selectedDate, 30);
    const periodEnd = selectedDate;
    const schoolDays = eachDayOfInterval({ start: periodStart, end: periodEnd }).filter(day => !isWeekend(day));

    switch (period) {
      case 'daily':
        return schoolDays.slice(-7).map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const dayRecords = attendanceRecords.filter(r => r.date === dateStr);
          const present = dayRecords.filter(r => r.status === 'present').length;
          const absent = dayRecords.filter(r => r.status === 'absent').length;
          const total = present + absent;
          return {
            name: format(day, 'EEE'),
            attendance: total > 0 ? Math.round((present / total) * 100) : 0,
            present,
            absent,
          };
        });
      case 'weekly':
        const weeks = [];
        let currentStart = new Date(periodStart);
        while (currentStart <= periodEnd) {
          const weekEnd = new Date(currentStart);
          weekEnd.setDate(currentStart.getDate() + 6);
          const effectiveEnd = new Date(Math.min(weekEnd.getTime(), periodEnd.getTime()));
          const weekRecords = attendanceRecords.filter(r => {
            const recordDate = new Date(r.date);
            return recordDate >= currentStart && recordDate <= effectiveEnd;
          });
          const present = weekRecords.filter(r => r.status === 'present').length;
          const absent = weekRecords.filter(r => r.status === 'absent').length;
          const total = present + absent;
          weeks.push({
            name: `Week ${weeks.length + 1}`,
            attendance: total > 0 ? Math.round((present / total) * 100) : 0,
            present,
            absent,
          });
          currentStart.setDate(currentStart.getDate() + 7);
        }
        return weeks;
      case 'monthly':
        return Array.from({ length: 12 }, (_, i) => {
          const month = new Date(selectedDate.getFullYear(), i, 1);
          const monthRecords = attendanceRecords.filter(r => {
            const recordDate = new Date(r.date);
            return recordDate.getMonth() === i && recordDate.getFullYear() === selectedDate.getFullYear();
          });
          const present = monthRecords.filter(r => r.status === 'present').length;
          const absent = monthRecords.filter(r => r.status === 'absent').length;
          const total = present + absent;
          return {
            name: format(month, 'MMM'),
            attendance: total > 0 ? Math.round((present / total) * 100) : 0,
            present,
            absent,
          };
        });
      case 'yearly':
        return Array.from({ length: 5 }, (_, i) => {
          const year = selectedDate.getFullYear() - 4 + i;
          const yearRecords = attendanceRecords.filter(r => new Date(r.date).getFullYear() === year);
          const present = yearRecords.filter(r => r.status === 'present').length;
          const absent = yearRecords.filter(r => r.status === 'absent').length;
          const total = present + absent;
          return {
            name: `${year}`,
            attendance: total > 0 ? Math.round((present / total) * 100) : 0,
            present,
            absent,
          };
        });
    }
  };

  // Generate class trend data for detail view
  const classTrendData = useMemo(() => {
    if (!selectedClass) return [];
    const periodStart = subDays(selectedDate, 30);
    const periodEnd = new Date(selectedDate);
    const schoolDays = eachDayOfInterval({ start: periodStart, end: periodEnd }).filter(day => !isWeekend(day));

    return schoolDays.map((day, i) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayRecords = attendanceRecords.filter(r => r.date === dateStr && r.classId === selectedClass);
      const present = dayRecords.filter(r => r.status === 'present').length;
      const absent = dayRecords.filter(r => r.status === 'absent').length;
      const total = present + absent;
      return {
        day: i + 1,
        attendance: total > 0 ? Math.round((present / total) * 100) : 0,
      };
    });
  }, [attendanceRecords, selectedClass, selectedDate]);

  // Export functions
  const exportToCSV = (data: any[], filename: string) => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + Object.keys(data[0]).join(",") + "\n"
      + data.map(row => Object.values(row).map(v => `"${v}"`).join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportReport = () => {
    const reportData = classAnalytics.map(cls => ({
      Class: cls.name,
      Teacher: cls.teacher,
      Students: cls.studentCount,
      'Attendance %': cls.avgAttendance,
      Status: cls.avgAttendance >= 90 ? 'Good' : 'Needs Attention',
    }));
    exportToCSV(reportData, 'attendance_report');
  };

  const handleExportClassReport = () => {
    const selectedClassData = classAnalytics.find(c => c.id === selectedClass);
    if (selectedClassData) {
      const classReportData = [
        {
          Class: selectedClassData.name,
          Teacher: selectedClassData.teacher,
          'Total Students': selectedClassData.studentCount,
          'Average Attendance': `${selectedClassData.avgAttendance}%`,
          'High Performers': studentAnalytics.filter(s => s.attendance >= 95).length,
          'At Risk Students': studentAnalytics.filter(s => s.attendance < 85).length,
        },
        ...studentAnalytics.map(student => ({
          'Reg Number': student.registrationNumber,
          'Student Name': student.name,
          'Attendance %': `${student.attendance}%`,
          'Days Present': student.daysPresent,
          'Days Absent': student.daysAbsent,
          Performance: student.attendance >= 95 ? 'Excellent' : 
                      student.attendance >= 90 ? 'Good' : 
                      student.attendance >= 80 ? 'Fair' : 'Poor',
        })),
      ];
      exportToCSV(classReportData, `${selectedClassData.name.replace(/\s+/g, '_')}_class_report`);
    }
  };

  // Alert classes
  const getAlertClasses = () => {
    return classAnalytics.filter(cls => cls.avgAttendance < 90);
  };

  // Helper functions for UI
  const getAttendanceColor = (percentage: number) => {
    if (percentage >= 95) return 'text-green-600';
    if (percentage >= 90) return 'text-blue-600';
    if (percentage >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAttendanceBadge = (percentage: number) => {
    if (percentage >= 95) return { variant: 'default' as const, label: 'Excellent', class: 'bg-green-100 text-green-800' };
    if (percentage >= 90) return { variant: 'secondary' as const, label: 'Good', class: 'bg-blue-100 text-blue-800' };
    if (percentage >= 80) return { variant: 'outline' as const, label: 'Fair', class: 'bg-yellow-100 text-yellow-800' };
    return { variant: 'destructive' as const, label: 'Poor', class: 'bg-red-100 text-red-800' };
  };

  const getDetailAttendanceColor = (percentage: number) => {
    if (percentage >= 95) return 'text-green-600 dark:text-green-400';
    if (percentage >= 90) return 'text-blue-600 dark:text-blue-400';
    if (percentage >= 80) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getDetailAttendanceBadge = (percentage: number) => {
    if (percentage >= 95) return { variant: 'default' as const, label: 'Excellent', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' };
    if (percentage >= 90) return { variant: 'secondary' as const, label: 'Good', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' };
    if (percentage >= 80) return { variant: 'outline' as const, label: 'Fair', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' };
    return { variant: 'destructive' as const, label: 'Poor', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' };
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'trends':
        const trendData = generateTrendData(selectedPeriod);
        const classComparisonData = classAnalytics.map(cls => ({
          name: cls.name,
          attendance: cls.avgAttendance,
          students: cls.studentCount,
        }));
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const todayRecords = attendanceRecords.filter(r => r.date === dateStr);
        const presentToday = todayRecords.filter(r => r.status === 'present').length;
        const absentToday = todayRecords.filter(r => r.status === 'absent').length;
        const totalToday = presentToday + absentToday;
        const pieData = [
          { name: 'Present', value: totalToday > 0 ? Math.round((presentToday / totalToday) * 100) : 0, color: '#10b981' },
          { name: 'Absent', value: totalToday > 0 ? Math.round((absentToday / totalToday) * 100) : 0, color: '#f59e0b' },
        ];
        const chartConfigTrends = {
          attendance: { label: "Attendance %", color: "#3b82f6" },
          present: { label: "Present", color: "#10b981" },
          absent: { label: "Absent", color: "#f59e0b" },
        };
        return (
          <div className="space-y-3 sm:space-y-4 md:space-y-6 w-full overflow-x-hidden">
            <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-3 w-full max-w-full">
              <Card className="m-3 col-span-1 lg:col-span-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="p-3 sm:p-4 md:p-6">
                  <CardTitle className="flex items-center gap-1 sm:gap-2 text-sm sm:text-base md:text-lg">
                    <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 flex-shrink-0" />
                    <span className="truncate">Attendance Trends ({selectedPeriod})</span>
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    School-wide attendance percentage over time
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                  <ChartContainer config={chartConfigTrends} className="w-full">
                    <ResponsiveContainer width="100%" height={250} minWidth={0}>
                      <LineChart data={trendData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                          width={40}
                        />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line 
                          type="monotone" 
                          dataKey="attendance" 
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={{ fill: "#3b82f6", strokeWidth: 2, r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </CardContent>
              </Card>

              <Card className="m-3 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="p-3 sm:p-4 md:p-6">
                  <CardTitle className="flex items-center gap-1 sm:gap-2 text-sm sm:text-base md:text-lg">
                    <PieChartIcon className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 flex-shrink-0" />
                    <span className="truncate">Today's Distribution</span>
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Present vs Absent ratio
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                  <ChartContainer config={chartConfigTrends} className="w-full">
                    <ResponsiveContainer width="100%" height={180} minWidth={0}>
                      <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={70}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <ChartTooltip content={<ChartTooltipContent />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-center sm:gap-3 mt-3 sm:mt-4">
                    {pieData.map((entry) => (
                      <div key={entry.name} className="flex items-center gap-1 sm:gap-2">
                        <div 
                          className="w-2 h-2 sm:w-3 sm:h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-xs sm:text-sm font-medium break-words">{entry.name}: {entry.value}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="col-span-1 lg:col-span-3 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="p-3 sm:p-4 md:p-6">
                  <CardTitle className="flex items-center gap-1 sm:gap-2 text-sm sm:text-base md:text-lg">
                    <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 flex-shrink-0" />
                    <span className="truncate">Class Comparison</span>
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Average attendance by class
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                  <ChartContainer config={chartConfigTrends} className="w-full">
                    <ResponsiveContainer width="100%" height={250} minWidth={0}>
                      <BarChart data={classComparisonData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                          width={40}
                        />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar 
                          dataKey="attendance" 
                          fill="#3b82f6" 
                          radius={[2, 2, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      case 'detail':
        const selectedClassData = classAnalytics.find(c => c.id === selectedClass);
        if (!selectedClassData) {
          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No class data for selectedClass:`, selectedClass);
          return <div>No class selected or data not available.</div>;
        }

        const sortedStudents = [...studentAnalytics].sort((a, b) => {
          if (sortBy === 'attendance') {
            return b.attendance - a.attendance;
          }
          return a.name.localeCompare(b.name);
        });

        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Rendering sortedStudents:`, sortedStudents);

        return (
          <div className="space-y-4 md:space-y-6 ">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col gap-3 xs:flex-row xs:items-center xs:gap-4 ">
                <Button variant="outline" onClick={() => setViewMode('overview')} className="flex items-center gap-2 w-full xs:w-auto">
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden xs:inline">Back to Overview</span>
                  <span className="xs:hidden">Back</span>
                </Button>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">{selectedClassData.name}</h2>
                  <p className="text-muted-foreground text-sm md:text-base">Teacher: {selectedClassData.teacher}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleExportClassReport} className="w-full md:w-auto">
                <Download className="mr-2 h-4 w-4" />
                <span className="hidden xs:inline">Export Class Report</span>
                <span className="xs:hidden">Export</span>
              </Button>
            </div>

            <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-4">
                  <CardTitle className="text-xs md:text-sm font-medium">Class Average</CardTitle>
                  <TrendingUp className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="text-lg md:text-2xl font-bold">{selectedClassData.avgAttendance}%</div>
                  <p className="text-xs text-muted-foreground">Attendance rate</p>
                </CardContent>
              </Card>

              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-4">
                  <CardTitle className="text-xs md:text-sm font-medium">Total Students</CardTitle>
                  <Users className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="text-lg md:text-2xl font-bold">{selectedClassData.studentCount}</div>
                  <p className="text-xs text-muted-foreground">Enrolled</p>
                </CardContent>
              </Card>

              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-4">
                  <CardTitle className="text-xs md:text-sm font-medium">High Performers</CardTitle>
                  <TrendingUp className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="text-lg md:text-2xl font-bold">
                    {studentAnalytics.filter(s => s.attendance >= 95).length}
                  </div>
                  <p className="text-xs text-muted-foreground">≥95% attendance</p>
                </CardContent>
              </Card>

              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-4">
                  <CardTitle className="text-xs md:text-sm font-medium">At Risk</CardTitle>
                  <Users className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="text-lg md:text-2xl font-bold">
                    {studentAnalytics.filter(s => s.attendance < 85).length}
                  </div>
                  <p className="text-xs text-muted-foreground">&lt;85% attendance</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
              <CardHeader className="p-3 sm:p-4 md:p-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <CalendarIcon className="h-4 w-4 md:h-5 md:w-5" />
                  30-Day Attendance Trend
                </CardTitle>
                <CardDescription className="text-sm">
                  Daily attendance percentage for {selectedClassData.name}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                <ChartContainer config={{ attendance: { label: "Attendance %", color: "#3b82f6" } }}>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={classTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="day" 
                        tick={{ fontSize: 12, fill: "#6b7280" }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: "#6b7280" }}
                        tickLine={false}
                        axisLine={false}
                        domain={[70, 100]}
                      />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Line 
                        type="monotone" 
                        dataKey="attendance" 
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
              <CardHeader className="p-3 sm:p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Student Attendance Records</CardTitle>
                <CardDescription className="text-sm">
                  Individual student performance in {selectedClassData.name}
                </CardDescription>
                <div className="flex flex-col gap-2 xs:flex-row xs:gap-2">
                  <Button 
                    variant={sortBy === 'attendance' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setSortBy('attendance')}
                    className="text-xs w-full xs:w-auto"
                  >
                    Sort by Attendance
                  </Button>
                  <Button 
                    variant={sortBy === 'name' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setSortBy('name')}
                    className="text-xs w-full xs:w-auto"
                  >
                    Sort by Name
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-2 sm:p-3 md:p-6 overflow-hidden">
                <div className="overflow-x-auto -mx-2 sm:-mx-3 md:-mx-6">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden">
                      {sortedStudents.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                          No student data available for this class. Please ensure a class is selected and students are fetched.
                        </div>
                      ) : (
                        <Table className="min-w-full">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3">Reg No.</TableHead>
                              <TableHead className="text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3 min-w-[120px]">Student Name</TableHead>
                              <TableHead className="text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3 min-w-[100px]">Attendance %</TableHead>
                              <TableHead className="text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3 hidden sm:table-cell min-w-[90px]">Days Present</TableHead>
                              <TableHead className="text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3 hidden sm:table-cell min-w-[90px]">Days Absent</TableHead>
                              <TableHead className="text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3 min-w-[90px]">Performance</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortedStudents.map((student) => {
                              const badge = getDetailAttendanceBadge(student.attendance);
                              return (
                                <TableRow key={student.id}>
                                  <TableCell className="font-medium text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3">{student.registrationNumber || 'N/A'}</TableCell>
                                  <TableCell className="text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0">
                                          {student.photoUrl ? (
                                              <img src={student.photoUrl} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                              <span className="text-[10px] font-bold text-slate-500">
                                                  {student.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                              </span>
                                          )}
                                      </div>
                                      <div className="truncate max-w-[120px] sm:max-w-none" title={student.name}>
                                        {student.name || 'Unknown'}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="px-2 sm:3 md:px-4 py-2 sm:py-3">
                                    <div className="space-y-1 min-w-[80px]">
                                      <div className={`font-medium text-[10px] sm:text-xs md:text-sm ${getDetailAttendanceColor(student.attendance)}`}>
                                        {student.attendance}%
                                      </div>
                                      <Progress value={student.attendance} className="h-1 w-12 sm:w-16 md:w-20" />
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3 hidden sm:table-cell">{student.daysPresent}</TableCell>
                                  <TableCell className="text-[10px] sm:text-xs md:text-sm px-2 sm:px-3 md:px-4 py-2 sm:py-3 hidden sm:table-cell">{student.daysAbsent}</TableCell>
                                  <TableCell className="px-2 sm:px-3 md:px-4 py-2 sm:py-3">
                                    <Badge className={`text-[10px] sm:text-xs ${badge.class} whitespace-nowrap`}>
                                      {badge.label}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      default:
        const overallAttendance = classAnalytics.length > 0
          ? Math.round(classAnalytics.reduce((sum, cls) => sum + cls.avgAttendance, 0) / classAnalytics.length)
          : 0;
        const totalStudents = students.length;

        return (
          <div className="space-y-4 md:space-y-6 w-full overflow-x-hidden">
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-4">
                  <CardTitle className="text-xs sm:text-sm font-medium">Overall Attendance</CardTitle>
                  <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="text-lg sm:text-xl md:tex-2xl font-bold">{overallAttendance}%</div>
                  <p className="text-xs text-muted-foreground">School average</p>
                </CardContent>
              </Card>

              <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-4">
                  <CardTitle className="text-xs sm:text-sm font-medium">Total Students</CardTitle>
                  <Users className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="text-lg sm:text-xl md:text-2xl font-bold">{totalStudents}</div>
                  <p className="text-xs text-muted-foreground">Across all classes</p>
                </CardContent>
              </Card>

              <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-4">
                  <CardTitle className="text-xs sm:text-sm font-medium">Classes</CardTitle>
                  <Users className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="text-lg sm:text-xl md:text-2xl font-bold">{classAnalytics.length}</div>
                  <p className="text-xs text-muted-foreground">Total classes</p>
                </CardContent>
              </Card>

              <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-4">
                  <CardTitle className="text-xs sm:text-sm font-medium">High Performers</CardTitle>
                  <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="text-lg sm:text-xl md:text-2xl font-bold">
                    {classAnalytics.filter(c => c.avgAttendance >= 95).length}
                  </div>
                  <p className="text-xs text-muted-foreground">≥95% attendance</p>
                </CardContent>
              </Card>
            </div>

            <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
              <CardHeader className="p-3 sm:p-4 md:p-6">
                <CardTitle className="text-sm sm:text-base md:text-lg">Class Performance</CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Attendance overview for all classes
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                <div className="space-y-3 sm:space-y-4 ">
                  {classAnalytics.map((classItem) => {
                    const badge = getAttendanceBadge(classItem.avgAttendance);
                    return (
                      <div
                        key={classItem.id}
                        className="m-2 flex flex-col border-[#E2E8F0] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929] md:flex-row md:items-center md:justify-between p-3 sm:p-4 border rounded-lg hover:border-muted-foreground/20 transition-all duration-200 space-y-3 md:space-y-0"
                      >
                        <div className="flex-1 space-y-2">
                          <div className="flex flex-col xs:flex-row xs:items-center gap-2 xs:gap-3">
                            <h3 className="font-semibold text-sm sm:text-base hover:text-foreground">{classItem.name}</h3>
                            <Badge className={`${badge.class} text-xs sm:text-sm w-fit`}>
                              {badge.label}
                            </Badge>
                          </div>
                          <div className="flex flex-col xs:flex-row xs:items-center gap-2 xs:gap-4 text-xs sm:text-sm text-muted-foreground hover:text-foreground/80">
                            <span>Teacher: {classItem.teacher}</span>
                            <span>Students: {classItem.studentCount}</span>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs sm:text-sm">
                              <span className="text-muted-foreground hover:text-foreground/80">Attendance Rate</span>
                              <span className={getAttendanceColor(classItem.avgAttendance)}>
                                {classItem.avgAttendance}%
                              </span>
                            </div>
                            <Progress value={classItem.avgAttendance} className="h-2" />
                          </div>
                        </div>
                        <div className="md:ml-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setSelectedClass(classItem.id); setViewMode('detail'); }}
                            className="flex mt-2 items-center gap-2 w-full xs:w-auto text-xs sm:text-sm hover:bg-primary hover:text-primary-foreground"
                          >
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="hidden xs:inline">View Details</span>
                            <span className="xs:hidden">Details</span>
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (networkError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <p className="text-lg text-red-600">Network error occurred. Please check your internet connection and try again.</p>
        <Button
          onClick={() => {
            setIsLoading(true);
            setNetworkError(false);
            auth.currentUser?.getIdToken(true);
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!hasValidClaims) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-lg text-red-600">No valid permissions to view attendance analytics.</p>
      </div>
    );
  }

  const alertClasses = getAlertClasses();

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <Header />
          <main className="flex-1 overflow-y-auto p-2 sm:p-4 md:p-6 lg:p-8 pt-3 sm:pt-4 md:pt-6">
            <div className="space-y-3 sm:space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
              <div className="flex flex-col space-y-2 sm:space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-tight break-words">Attendance Analytics</h2>
                  <p className="text-muted-foreground text-xs sm:text-sm md:text-base break-words">
                    Monitor and analyze school-wide attendance patterns
                  </p>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs sm:text-sm w-full sm:w-auto min-w-0"
                    onClick={handleExportReport}
                  >
                    <Download className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span className="hidden sm:inline truncate">Export Report</span>
                    <span className="sm:hidden">Export</span>
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:gap-3 md:gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full sm:w-auto justify-start text-left font-normal text-xs sm:text-sm min-w-0")}>
                        <CalendarIcon className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="hidden sm:inline truncate">{format(selectedDate, "MMM d, yyyy")}</span>
                        <span className="sm:hidden truncate">{format(selectedDate, "MMM d")}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-50" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        initialFocus
                        className="pointer-events-auto rounded-lg border shadow-sm"
                      />
                    </PopoverContent>
                  </Popover>

                  <Select value={selectedPeriod} onValueChange={(value: 'daily' | 'weekly' | 'monthly' | 'yearly') => setSelectedPeriod(value)}>
                    <SelectTrigger className="w-full sm:w-28 md:w-32 bg-[#F9FAFB] dark:bg-[#020817] text-xs sm:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-50 bg-[#F9FAFB] dark:bg-[#020817]">
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Tabs
                  value={viewMode}
                  onValueChange={(value) => setViewMode(value as 'overview' | 'trends' | 'detail')}
                  className="w-full lg:w-auto h-8 sm:h-9 "
                >
                  <TabsList className="grid w-full grid-cols-2 lg:w-auto bg-[#F9FAFB] dark:bg-[#1E293B] rounded-md p-1">
                    <TabsTrigger
                      value="overview"
                      className="text-xs sm:text-sm px-1 sm:px-2 md:px-3 rounded-md transition-colors duration-200 
                      data-[state=active]:bg-white data-[state=active]:dark:bg-[#020817] 
                      data-[state=active]:text-[#1E293B] data-[state=active]:dark:text-white 
                      text-[#64748B] dark:text-[#94A3B8] hover:bg-gray-200 dark:hover:bg-[#2D3A4F]"
                    >
                      Overview
                    </TabsTrigger>
                    <TabsTrigger
                      value="trends"
                      className="text-xs sm:text-sm px-1 sm:px-2 md:px-3 rounded-md transition-colors duration-200 
                      data-[state=active]:bg-white data-[state=active]:dark:bg-[#020817] 
                      data-[state=active]:text-[#1E293B] data-[state=active]:dark:text-white 
                      text-[#64748B] dark:text-[#94A3B8] hover:bg-gray-200 dark:hover:bg-[#2D3A4F]"
                    >
                      Trends
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {alertClasses.length > 0 && viewMode === 'overview' && (
                <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950 overflow-hidden m-3">
                  <CardHeader className="p-3 sm:p-4 md:p-6 pb-2 sm:pb-3">
                    <CardTitle className="flex items-center gap-1 sm:gap-2 text-yellow-800 dark:text-yellow-200 text-sm sm:text-base md:text-lg">
                      <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 flex-shrink-0" />
                      <span className="truncate">Attendance Alerts</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                    <div className="flex flex-wrap gap-1 sm:gap-2 max-w-full">
                      {alertClasses.map(cls => (
                        <Badge 
                          key={cls.id} 
                          variant="outline" 
                          className="bg-white dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700 text-[10px] sm:text-xs flex-shrink-0 max-w-full"
                        >
                          <span className="hidden sm:inline truncate">{cls.name}: {cls.avgAttendance}%</span>
                          <span className="sm:hidden truncate">{cls.name}</span>
                        </Badge>
                      ))}
                    </div>
                    <p className="text-[10px] sm:text-xs md:text-sm text-yellow-700 dark:text-yellow-300 mt-2 break-words">
                      Classes with attendance below 90% threshold
                    </p>
                  </CardContent>
                </Card>
              )}

              <div className="min-h-0 flex-1 w-full overflow-hidden">
                <div className="w-full max-w-full overflow-x-auto">
                  {renderContent()}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default AdminAttendanceAnalytics;